import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "./api";
import { useAuth } from "./auth";

const TeamContext = createContext(null);

// Default season is the current sports season "YYYY/YYYY" (Aug-Jul).
export function currentSportsSeason(date = new Date()) {
  const y = date.getFullYear();
  const m = date.getMonth(); // 0-11
  // Italian sports season starts Aug (month 7)
  if (m >= 7) return `${y}/${y + 1}`;
  return `${y - 1}/${y}`;
}

// Generate a list of seasons around the current one
export function listSeasons(date = new Date(), past = 3, future = 3) {
  const cur = currentSportsSeason(date);
  const [a] = cur.split("/").map(Number);
  const seasons = [];
  for (let i = -past; i <= future; i++) {
    const start = a + i;
    seasons.push(`${start}/${start + 1}`);
  }
  return seasons.reverse(); // most recent first
}

// Extract the 4-digit year from a team category string (e.g. "Pulcini 2014",
// "Under 12 - 2013"). Returns null if no year is detected.
export function extractBirthYear(category) {
  if (!category) return null;
  const m = String(category).match(/(19|20)\d{2}/);
  return m ? Number(m[0]) : null;
}

// Sort teams by birth year DESCENDING (bigger year first → younger category
// shown on top, e.g. 2014, 2014, 2012). Teams without a detectable year fall
// back to alphabetical order at the end of the list.
export function sortTeamsByAge(teams = []) {
  const withYear = [];
  const noYear = [];
  for (const t of teams) {
    const y = extractBirthYear(t.category);
    if (y != null) withYear.push({ t, y });
    else noYear.push(t);
  }
  withYear.sort((a, b) => {
    if (a.y !== b.y) return b.y - a.y; // DESC
    return (a.t.name || "").localeCompare(b.t.name || "", "it");
  });
  noYear.sort((a, b) => (a.name || "").localeCompare(b.name || "", "it"));
  return [...withYear.map((x) => x.t), ...noYear];
}

export function TeamProvider({ children }) {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(
    () => localStorage.getItem("activeTeamId") || ""
  );
  const [season, setSeasonState] = useState(
    () => localStorage.getItem("activeSeason") || currentSportsSeason()
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const { data } = await api.get("/teams");
      const sorted = sortTeamsByAge(data);
      setTeams(sorted);
      if (sorted.length && !sorted.find((t) => t.id === activeTeamId)) {
        setActiveTeamId(sorted[0].id);
        localStorage.setItem("activeTeamId", sorted[0].id);
      }
    } finally {
      setLoading(false);
    }
  }, [user, activeTeamId]);

  useEffect(() => {
    if (user && user !== false) refresh();
  }, [user, refresh]);

  const setActive = useCallback((id) => {
    setActiveTeamId(id);
    localStorage.setItem("activeTeamId", id);
  }, []);

  const setSeason = useCallback((s) => {
    setSeasonState(s);
    localStorage.setItem("activeSeason", s);
  }, []);

  // Custom seasons added by the user (persisted in localStorage). Format:
  // "YYYY/YYYY" e.g. "2030/2031". Merged with the auto-generated list.
  const [customSeasons, setCustomSeasons] = useState(() => {
    try {
      const raw = localStorage.getItem("customSeasons");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const addSeason = useCallback((label) => {
    const clean = String(label || "").trim();
    if (!/^\d{4}\/\d{4}$/.test(clean)) {
      throw new Error("Formato stagione non valido (es. 2030/2031)");
    }
    const [a, b] = clean.split("/").map(Number);
    if (b !== a + 1) {
      throw new Error("Gli anni devono essere consecutivi (es. 2030/2031)");
    }
    setCustomSeasons((prev) => {
      if (prev.includes(clean)) return prev;
      const next = [...prev, clean];
      try {
        localStorage.setItem("customSeasons", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    return clean;
  }, []);

  const activeTeam = useMemo(
    () => teams.find((t) => t.id === activeTeamId) || null,
    [teams, activeTeamId]
  );
  const canEdit = !activeTeam || activeTeam.my_role !== "parent";

  const seasons = useMemo(() => {
    const auto = listSeasons();
    const merged = new Set([...auto, ...customSeasons]);
    // Sort descending: "2030/2031" > "2029/2030" > ...
    return Array.from(merged).sort((a, b) => b.localeCompare(a));
  }, [customSeasons]);

  const value = useMemo(
    () => ({
      teams,
      activeTeam,
      activeTeamId,
      setActive,
      refresh,
      loading,
      canEdit,
      season,
      setSeason,
      seasons,
      addSeason,
    }),
    [teams, activeTeam, activeTeamId, setActive, refresh, loading, canEdit, season, setSeason, seasons, addSeason]
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export const useTeams = () => useContext(TeamContext);
